import { DOCUMENT } from '@angular/common';
import { Injectable, signal, computed, inject } from '@angular/core';

const FONT_RESOURCE_PATTERN = /\.(ttf|woff|woff2|eot|otf)(\?.*)?$/i;
const FONT_READY_TIMEOUT_MS = 5000;
const FONT_READY_UNSUPPORTED_FALLBACK_MS = 3000;

/**
 * Represents details of a font asset.
 */
export interface Asset {
  /** The URL of the asset file. */
  name: string;

  /** The time taken to load the asset, in milliseconds. */
  duration: number;

  /** The size of the asset file transferred, in bytes. */
  transferSize: number;
}

/**
 * Represents the state of font assets in the application.
 */
interface AssetStateModel {
  /** Indicates whether all fonts have been successfully loaded. */
  fontsLoaded: boolean;

  /** List of loaded font assets. */
  fonts: Asset[];
}

/**
 * A service to monitor and track the loading state of font assets.
 */
@Injectable({ providedIn: 'root' })
export class AssetMonitorStoreService {
  private readonly document = inject(DOCUMENT);

  /** Internal state to track the font loading process. */
  private readonly _state = signal<AssetStateModel>({
    fontsLoaded: false,
    fonts: [],
  });

  /** Publicly accessible computed state of font assets. */
  readonly $state = this._state.asReadonly();
  readonly $fontLoaded = computed(() => {
    return this.$state().fontsLoaded;
  });
  constructor() {
    // Initialize the monitoring of font assets when the service is created.
    this.init();
  }

  /**
   * Initializes the monitoring of font assets by checking existing loaded fonts
   * and setting up a `PerformanceObserver` to track new font resource loads.
   */
  private init(): void {
    // Check for fonts that are already loaded when the application starts.
    const existingFonts = this.getExistingFonts();

    // If any fonts are already loaded, update the internal state.
    if (existingFonts.length > 0) {
      this.updateFonts(existingFonts);
    }

    // Setup a `PerformanceObserver` to monitor new font loads.
    this.observeNewFonts();

    // Use the Font Loading API as the source of truth for font readiness.
    this.waitForFontsReady();
  }

  /**
   * Retrieves existing font resources from the `Performance` API.
   * @returns An array of font assets that are already loaded.
   */
  private getExistingFonts(): Asset[] {
    if (!this.hasPerformanceResourceTiming()) {
      return [];
    }

    return performance
      .getEntriesByType('resource')
      .filter(this.isFontResource)
      .map(this.mapResource);
  }

  private observeNewFonts(): void {
    if (!this.hasPerformanceObserverResourceTiming()) {
      return;
    }

    try {
      const observer = new PerformanceObserver(
        (list: PerformanceObserverEntryList) => {
          // Collect new font resources.
          const newFonts: Asset[] = list
            .getEntries() // Get all entries in the observer's buffer.
            .filter(this.isFontResource)
            .map(this.mapResource); // Map them to the `FontAsset` structure.

          // If new fonts are found, update the state.
          if (newFonts.length > 0) {
            this.updateFonts(newFonts);
          }
        }
      );

      // Observe resource loads, including buffered entries as the page reloads.
      observer.observe({ type: 'resource', buffered: true });
    } catch {
      // Resource timing is best-effort only; font readiness is handled separately.
    }
  }

  private waitForFontsReady(): void {
    const fontsReady = this.document.fonts?.ready;

    if (!fontsReady) {
      window.setTimeout(() => {
        this.setFontsLoaded(true);
      }, FONT_READY_UNSUPPORTED_FALLBACK_MS);
      return;
    }

    window.setTimeout(() => {
      this.setFontsLoaded(true);
    }, FONT_READY_TIMEOUT_MS);

    fontsReady
      .then(() => {
        this.setFontsLoaded(true);
      })
      .catch(() => {
        this.setFontsLoaded(true);
      });
  }

  private hasPerformanceResourceTiming(): boolean {
    return (
      typeof performance !== 'undefined' &&
      typeof performance.getEntriesByType === 'function'
    );
  }

  private hasPerformanceObserverResourceTiming(): boolean {
    return (
      typeof PerformanceObserver !== 'undefined' &&
      Array.isArray(PerformanceObserver.supportedEntryTypes) &&
      PerformanceObserver.supportedEntryTypes.includes('resource')
    );
  }

  private isFontResource(
    entry: PerformanceEntry
  ): entry is PerformanceResourceTiming {
    const resource = entry as PerformanceResourceTiming;

    return (
      entry.entryType === 'resource' &&
      typeof resource.name === 'string' &&
      (resource.initiatorType === 'font' ||
        FONT_RESOURCE_PATTERN.test(resource.name))
    );
  }

  private mapResource(resource: PerformanceResourceTiming): Asset {
    return {
      name: resource.name,
      duration: resource.duration,
      transferSize: resource.transferSize ?? 0,
    };
  }

  /**
   * Updates the internal state with newly loaded fonts.
   *
   * @param newFonts - An array of the newly loaded font assets.
   */
  private updateFonts(newFonts: Asset[]): void {
    this._state.update((state) => {
      const fontNames = new Set(state.fonts.map((font) => font.name));
      const fonts = [...state.fonts];

      newFonts.forEach((font) => {
        if (!fontNames.has(font.name)) {
          fontNames.add(font.name);
          fonts.push(font);
        }
      });

      return {
        ...state,
        fonts,
      };
    });
  }

  private setFontsLoaded(fontsLoaded: boolean): void {
    this._state.update((state) => {
      if (state.fontsLoaded === fontsLoaded) {
        return state;
      }

      return {
        ...state,
        fontsLoaded,
      };
    });
  }
}
